<template>
  <footer class="py-8 px-6">
    <div class="container">
      <a class="lg:hidden" :href="logoUrl">
        <MImage class="w-8/12 md:w-1/2" src="/images/logo.svg" alt="logo" />
      </a>
      <nav class="mt-4 lg:mt-0">
        <ul class="flex flex-col lg:flex-row">
          <li
            class="flex items-center menu-item relative py-2 lg:py-0"
            v-for="(item, i) in footer.menu"
            :key="i"
          >
            <a
              class="text-gray-400 font-bold hover:text-gray-600 transition-color duration-200"
              :href="item.link ?? undefined"
              @click="trackItemClick(item.link)"
              v-text="item.label"
            />
          </li>
        </ul>
      </nav>
      <div class="lg:mt-8 flex justify-between">
        <div>
          <i class="flag flag-ch -mb-0.5 hidden lg:inline-block" />
          <span class="text-gray-600 font-bold hidden lg:inline-block">{{
            $t('footer.made_in_switzerland')
          }}</span>
          <div class="caption mt-6 lg:text-16" v-html="$t('footer.address')" />
          <div class="caption mt-6 lg:text-16 py-2 border-t-2 border-gray-200">
            <strong>{{ $t('affiliate_disclosure.title') }}</strong>
            <p>{{ $t('affiliate_disclosure.text') }}</p>
          </div>
        </div>
        <ul v-if="!noSocial" class="lg:mt-8 hidden lg:flex">
          <li class="mx-2" v-for="(s, i) in socialLinks" :key="i">
            <a
              :href="s.link"
              class="social-link w-6 h-6 bg-center bg-no-repeat bg-contain block opacity-75 hover:opacity-100 transition-opacity duration-200"
              target="_blank"
              :style="{ backgroundImage: s.image }"
              v-text="s.label"
              rel="noopener"
            />
          </li>
        </ul>
      </div>
      <ul class="caption bottom-menu mt-6 flex flex-col lg:flex-row">
        <li class="flex items-center py-2 relative menu-item">
          <span class="menu_link"
            >© {{ newProject ? '' : '2013-' }}{{ new Date().getFullYear() }}</span
          >
        </li>
        <li
          class="menu-item flex items-center py-2 relative"
          v-for="(item, i) in footer.bottomMenu"
          :key="i"
        >
          <a :href="item.link ?? undefined" class="menu_link" v-text="item.label" />
        </li>
      </ul>
    </div>
  </footer>
</template>

<script lang="ts" setup>
import type { GQLPageFooter } from './generated/page-footer'
import { NavigationItemClick } from '@core/analytics/core-events/navigation'

defineProps<{
  footer: GQLPageFooter
  noSocial?: boolean
  newProject?: boolean
}>()

const socialLinks = [
  {
    icon: 'fb',
    label: 'Facebook page',
    link: 'http://www.facebook.com/Monitocom',
    image: 'url(/images/socials/fb.svg)',
  },
  {
    icon: 'twitter',
    label: 'Twitter page',
    link: 'https://twitter.com/monito',
    image: 'url(/images/socials/twitter.svg)',
  },
  {
    icon: 'linkedin',
    label: 'Likedin profile',
    link: 'https://www.linkedin.com/company/monito-com',
    image: 'url(/images/socials/linkedin.svg)',
  },
  {
    icon: 'youtube',
    label: 'Youtube channel',
    link: 'https://www.youtube.com/c/Monitocom',
    image: 'url(/images/socials/youtube.svg)',
  },
]

const { $track } = useNuxtApp()
const logoUrl = computed(() => getHomeUrl())

async function trackItemClick(url: string | null) {
  if (url) {
    await $track(NavigationItemClick(url, 'footer'))
  }
}
</script>

<style scoped>
@screen lg {
  .menu-item::after {
    @apply block relative bg-gray-400 right-0 mx-4;
    content: '';
    width: 2px;
    height: 60%;
  }

  .menu-item:last-child::after {
    @apply hidden;
  }

  .bottom-menu .menu-item::after {
    width: 1px;
  }

  .social-link {
    text-indent: -9999px;
  }
}
</style>
